import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Box,
  Group,
  Image,
  Text,
  Stack,
  Container,
  Grid,
  Title,
  MantineProvider,
  createStyles,
  ScrollArea,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Helmet } from "react-helmet";

import SpaceListItem from "../spaces_neo/presentations/SpaceListItem";
import Loading from "../spaces_neo/presentations/static/Loading";
import FollowsBlock from "./FollowsBlock";
import DefaultAvatar from "../../src/images/fallback/default.png";

import UserModel from "../../models/User";
import DirectMessageModel from "../../models/DirectMessage";
import SpaceNeo4jModel from "../../models/neo4j/Space";
import FollowModel from "../../models/Follow";

import { setHiddenSidebar } from "../../store/app";

const useStyles = createStyles((theme) => ({
  userInfoWrap: {
    backgroundColor: theme.colors.gray[0],
    borderRadius: 10,
    padding: 40,
    paddingTop: 30,
    paddingBottom: 30,
  },
  userInfoMediumScreen: {
    justifyContent: "center",
  },
  avatarWrapMediumScreen: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  avatarMediumScreen: {
    width: 200,
    marginRight: 0,
  },
  avatar: {
    width: 300,
    marginRight: 30,
  },
  userDetailsWrap: {
    alignSelf: "center",
  },
  nameWrapMediumScreen: {
    justifyContent: "center",
  },
  nameWrap: {
    justifyContent: "flex-start",
  },
  buttonStyleOutline: {
    backgroundColor: "none",
    color: theme.colors.gray[6],
    border: `1px solid ${theme.colors.gray[6]}`,
    "&:hover": {
      backgroundColor: theme.colors.gray[1],
    },
  },
}));

const paginator = {
  per: 10,
  page: 0
};

export default function Profile() {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const mediumScreen = useMediaQuery(`(max-width: 768px)`);

  const user = useSelector((state) => state.user);
  
  const [profileUser, setProfileUser] = useState({});
  const [loading, setLoading] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [followInfo, setFollowInfo] = useState(null);
  const [loadingFollowInfo, setLoadingFollowInfo] = useState(false);

  const nomoreSpaces = useRef();

  useEffect(() => {
    dispatch(setHiddenSidebar({ hidden: true }));
    setTimeout(() => {
      dispatch(setHiddenSidebar({ hidden: true }));
    }, 1000);
    return () => {
      dispatch(setHiddenSidebar({ hidden: false }));
    };
  }, []);

  useEffect(() => {
    if (params.username) {
      onFetch();
      onFetchSpaces();
      onFetchFollowInfo();
    }
  }, [params]);

  const onFetch = () => {
    UserModel.onFetch(
      params.username,
      (data) => setProfileUser(data),
      (err) => {}
    );
  };

  const onFetchSpaces = () => {
    const ctrl = new AbortController();
    setLoading(true);
    SpaceNeo4jModel.onFetchByScope(
      {
        scope: "top",
        username: params.username,
        page: paginator.page,
        per: paginator.per
      },
      (data) => {
        if (Array.isArray(data)) {
          if (data.length > 0) {
            const merged = [...spaces, ...data];
            const unique = Array.from(
              new Map(merged.map((item) => [item.id, item])).values()
            );
            setSpaces(unique);
            if (data.length % paginator.per != 0) {
              nomoreSpaces.current = true;
            }
          } else {
            nomoreSpaces.current = true;
          }
        }
        setLoading(false);
      },
      (err) => setLoading(false),
      ctrl.signal
    );
  };

  const onFetchFollowInfo = () => {
    setLoadingFollowInfo(true);
    FollowModel.onFetchFollowInfo(
      { user_id: params.username },
      (data) => {
        setFollowInfo(data);
        setLoadingFollowInfo(false);
      },
      (data) => setLoadingFollowInfo(false)
    );
  };

  const onDirectMessage = () => {
    if (!user.authenticated) {
      navigate(`/user/start`);
      return;
    }
    DirectMessageModel.onFetchOrCreate(
      profileUser.id,
      (data) => {
        navigate(`/space/${data.space.slug}/discussion`);
      },
      (err) => {}
    );
  };

  const onPressFollow = () => {
    if (!profileUser?.id) return;
    const action = profileUser.is_followed ? FollowModel.onUnfollow : FollowModel.onFollow;
    action(
      profileUser.id,
      (data) => {
        if (data.success) {
          onFetch();
          onFetchFollowInfo();
        }
      },
      (err) => {}
    );
  };

  if (!user) {
    return null;
  }

  return (
    <ScrollArea style={{ height: "100%" }}>
      <Helmet>
        <title>
          {`Sutra - ${profileUser?.id ? profileUser.username : "Dashboard"}`}
        </title>
      </Helmet>
      <Container style={{ marginBottom: "50px" }} mt={20}>
        <Box className={classes.userInfoWrap}>
          <Group
            noWrap={mediumScreen ? false : true}
            className={mediumScreen ? classes.userInfoMediumScreen : ""}
          >
            <Box className={mediumScreen ? classes.avatarWrapMediumScreen : ""}>
              <Image
              className={mediumScreen ? classes.avatarMediumScreen : classes.avatar}
                radius={300}
                src={profileUser?.avatar?.url || DefaultAvatar}
                alt={profileUser.username}
              />
            </Box>
            <Box className={classes.userDetailsWrap}>
              <Stack spacing={0} align={mediumScreen ? "center" : "flex-start"}>
                <Group
                  spacing={0}
                  mb={10}
                  className={classes[mediumScreen ? "nameWrapMediumScreen" : "nameWrap"]}
                >
                  <Title sx={{ fontWeight: 900, marginRight: 10 }}>
                    {profileUser.full_name}
                  </Title>
                  <Text color="dimmed" size="xl">
                    @{profileUser.username}
                  </Text>
                </Group>
                <FollowsBlock
                  disablePopover={true}
                  user={profileUser}
                  followers_count={followInfo?.followers_count}
                  followees_count={followInfo?.followees_count}
                  prana_count={followInfo?.prana_count}
                  posts_in_count={followInfo?.posts_in_count}
                  likes_in_count={followInfo?.likes_in_count}
                  top_parent_name={followInfo?.top_parent_name}
                  onFetchFollowInfo={() => {
                    onFetch();
                    onFetchFollowInfo();
                  }}
                />
                {profileUser && user.id != profileUser.id ? (
                  <Group mt={10}>
                    <Button
                      sx={{ width: 200 }}
                      radius="xl"
                      size="md"
                      onClick={() => onDirectMessage()}
                    >
                      {t("users.profile.button")}
                    </Button>
                    {typeof profileUser?.is_followed === "boolean" && (
                      <Button
                        radius="xl"
                        size="md"
                        onClick={onPressFollow}
                      >
                        {profileUser.is_followed ? "Unfollow" : "Follow"}
                      </Button>
                    )}
                  </Group>
                ) : (
                  <MantineProvider theme={{ primaryShade: 6 }}>
                    <Button
                      mt={10}
                      color={"gray"}
                      sx={{ width: 200 }}
                      radius="xl"
                      size="md"
                      component={Link}
                      to="/settings/profile"
                    >
                      {t("users.profile.button2")}
                    </Button>
                  </MantineProvider>
                )}
              </Stack>
            </Box>
          </Group>
        </Box>

        {typeof profileUser?.bio === "string" && profileUser.bio.length > 0 && (
          <Text mt={30}>{profileUser.bio}</Text>
        )}

        {Array.isArray(spaces) && spaces.length > 0 && (
          <>
            <Group
              position="apart"
              mt={30}
              mb={15}
              pb={10}
              sx={(theme) => ({ borderBottom: `1px solid ${theme.colors.gray[2]}` })}
            >
              <div>
                <Title order={2} sx={{ fontWeight: 900 }}>
                  {t("users.profile.title")}
                </Title>
              </div>
            </Group>
            <Box mb={60}>
              <Grid>
                {spaces.map((space) => {
                  return (
                    <Grid.Col sm={12} md={6}>
                      <SpaceListItem
                        key={`space-list-item-${space.slug}`}
                        space={space}
                        lineClamp={2}
                        styleTitle={{ fontSize: "1.4em" }}
                      />
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Box>
            {!nomoreSpaces.current && (
              <MantineProvider theme={{ primaryShade: 8 }}>
                {loading ? (
                  <Loading />
                ) : (
                  <Button
                    variant="outline"
                    className={classes.buttonStyleOutline}
                    radius="xl"
                    mt={30}
                    style={{ left: "50%", transform: "translateX(-50%)" }}
                    onClick={() => {
                      paginator.page++;
                      onFetchSpaces(profileUser);
                    }}
                  >
                    {"Load more"}
                  </Button>
                )}
              </MantineProvider>
            )}
          </>
        )}
        
        <Box sx={{height: "100px"}} />
      </Container>
    </ScrollArea>
  );
}
